import React, { useState } from "react";
import { Check, Circle, Loader2, GripVertical } from "lucide-react";

// Horizontal milestone tracker. When `onMove` is supplied (admin), the current
// stage marker can be dragged and dropped on any other stage; onMove(toKey) fires.
export default function OnboardingTracker({ milestones = [], compact = false, onMove }) {
  const [dragging, setDragging] = useState(false);
  const [over, setOver] = useState(null);
  if (!milestones.length) return null;

  const nodeSize = compact ? 22 : 30;
  const iconSize = compact ? 12 : 15;
  const canDrag = typeof onMove === "function";

  return (
    <div className="w-full" data-testid="onboarding-tracker">
      {canDrag && <div className="text-[10px] text-[#6C757D] mb-2 flex items-center gap-1"><GripVertical size={10} /> Drag the gold stage marker to another stage to move this dealer (a reason will be requested).</div>}
      <div className="flex items-start gap-0">
        {milestones.map((m, i) => {
          const isLast = i === milestones.length - 1;
          const next = milestones[i + 1];
          const railComplete = m.status === "complete";
          const isCurrent = m.status === "current";
          const dropTarget = canDrag && dragging && !isCurrent;
          return (
            <React.Fragment key={m.key}>
              <div className="flex flex-col items-center min-w-0 shrink-0" style={{ width: compact ? 96 : 128 }}
                onDragOver={(e) => { if (dropTarget) { e.preventDefault(); setOver(m.key); } }}
                onDragLeave={() => setOver((o) => (o === m.key ? null : o))}
                onDrop={(e) => { if (dropTarget) { e.preventDefault(); setOver(null); setDragging(false); onMove(m.key); } }}
                data-testid={`tracker-drop-${m.key}`}>
                <div
                  data-testid={`tracker-node-${m.key}`}
                  draggable={canDrag && isCurrent}
                  onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", m.key); setDragging(true); }}
                  onDragEnd={() => { setDragging(false); setOver(null); }}
                  title={canDrag && isCurrent ? "Drag to another stage" : undefined}
                  className={`relative flex items-center justify-center rounded-full transition-all ${
                    m.status === "complete"
                      ? "bg-[#0B132B] text-white"
                      : isCurrent
                        ? `bg-[#D4AF37] text-[#0B132B] ring-4 ring-[#D4AF37]/20 ${canDrag ? "cursor-grab active:cursor-grabbing hover:scale-110" : ""}`
                        : "bg-white text-[#CED4DA] border-2 border-[#E9ECEF]"
                  } ${over === m.key ? "scale-125 ring-4 ring-[#D4AF37]/50 border-[#D4AF37]" : dropTarget ? "border-dashed border-[#D4AF37]" : ""}`}
                  style={{ width: nodeSize, height: nodeSize }}
                >
                  {m.status === "complete" ? <Check size={iconSize} strokeWidth={3} />
                    : isCurrent ? <Loader2 size={iconSize} className="animate-spin" strokeWidth={2.5} />
                    : <Circle size={iconSize - 4} strokeWidth={2} />}
                </div>
                <div className={`mt-1.5 text-center ${compact ? "text-[9px]" : "text-[10px]"} uppercase tracking-[0.14em] font-bold ${
                  m.status === "future" ? "text-[#ADB5BD]" : "text-[#0B132B]"
                } ${over === m.key ? "text-[#D4AF37]" : ""}`}>
                  {m.label}
                </div>
                {!compact && m.subtitle && (
                  <div className={`text-[10px] mt-0.5 text-center leading-tight line-clamp-2 ${
                    m.status === "future" ? "text-[#CED4DA]" : "text-[#6C757D]"
                  }`}>
                    {m.subtitle}
                  </div>
                )}
              </div>
              {!isLast && (
                <div className="flex-1 mt-[10px] shrink" data-testid={`tracker-rail-${m.key}`}>
                  <div className={`h-[3px] rounded-full transition-all ${
                    railComplete ? "bg-[#0B132B]" : next?.status === "current" ? "bg-gradient-to-r from-[#0B132B] to-[#D4AF37]" : "bg-[#E9ECEF]"
                  }`} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
